/**
 * Janela da sala FORA da guia: a Document Picture-in-Picture do navegador (Chrome e Edge no desktop)
 * abre uma janelinha que fica por cima dos outros programas e aceita qualquer HTML, não só o vídeo.
 * A sala continua rodando nesta guia (a conexão com o LiveKit não muda): o React só desenha a mesma
 * árvore dentro da janela, por um portal. Safari e Firefox não têm essa API e seguem com o
 * picture-in-picture do vídeo do outro participante.
 */

/** Tamanho inicial da janela (a pessoa pode redimensionar). */
// 360 de altura: o cartão do convite da janela compacta (a sala é sempre compacta aqui) cabe acima da prévia.
export const POPOUT_SIZE = Object.freeze({ width: 460, height: 360 });

/** Altura que a gaveta da transcrição (o painel nas janelas compactas) acrescenta à janela, para o vídeo não encolher. */
export const DOCK_PANEL_HEIGHT = 300;

export const isPopoutSupported = () =>
    typeof window !== 'undefined' && typeof window.documentPictureInPicture?.requestWindow === 'function';

/** Copia os estilos da página para a janela: Tailwind e tokens de cor valem lá dentro também. */
export function copyStyles(from, to) {
    for (const sheet of Array.from(from.styleSheets)) {
        try {
            const style = to.createElement('style');
            style.textContent = Array.from(sheet.cssRules).map((rule) => rule.cssText).join('');
            to.head.appendChild(style);
        } catch {
            // Folha de outra origem não deixa ler as regras: a janela carrega a mesma URL.
            if (!sheet.href) continue;
            const link = to.createElement('link');
            link.rel = 'stylesheet';
            link.href = sheet.href;
            if (sheet.media?.mediaText) link.media = sheet.media.mediaText;
            to.head.appendChild(link);
        }
    }
}

/**
 * Abre a janela. Só funciona dentro de um gesto do usuário (clique, tecla): sem ele o navegador recusa.
 * @returns {Promise<{ window: Window, container: HTMLElement }>} `container` é onde a sala é desenhada.
 */
export async function openPopoutWindow({ width, height } = POPOUT_SIZE) {
    const popout = await window.documentPictureInPicture.requestWindow({ width, height });
    const doc = popout.document;
    copyStyles(document, doc);
    doc.documentElement.lang = document.documentElement.lang || 'pt-BR';
    doc.documentElement.style.height = '100%';
    Object.assign(doc.body.style, { margin: '0', height: '100%', overflow: 'hidden', background: '#000' });
    const container = doc.createElement('div');
    container.className = 'flex h-full min-h-0 w-full flex-col';
    doc.body.appendChild(container);
    return { window: popout, container };
}
